import React, { useState } from 'react';
import {
  Loader2,
  Mail,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';

const ACTIVE_STATES = new Set(['queued', 'running']);

const normalizeDate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const date = new Date(hasTimezone ? normalized : `${normalized}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = normalizeDate(value);
  if (!date) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const mailboxStateCopy = (state) => {
  const normalized = String(state || 'never').toLowerCase();
  if (normalized === 'queued') return 'Waiting to check';
  if (normalized === 'running') return 'Checking mail';
  if (normalized === 'complete') return 'Available';
  if (normalized === 'failed') return 'Needs attention';
  if (normalized === 'reconnect_required') return 'Reconnect Outlook';
  return 'Not checked yet';
};

const OutlookIntegrationPanel = ({
  connection,
  mailboxStatus = null,
  loading = false,
  working = '',
  confirmingDisconnect = false,
  onConnect,
  onRequestDisconnect,
  onCancelDisconnect,
  onConfirmDisconnect,
  onCheckNow,
}) => {
  const connected = Boolean(connection?.connected);
  const unavailable = connection?.status === 'unavailable';
  const requiresReconnect = Boolean(connection?.requires_reconnect);
  const controlsDisabled = !connected || requiresReconnect || unavailable || Boolean(working);
  const resolvedMailboxStatus = mailboxStatus || { state: 'never' };
  const [aiProcessingAcknowledged, setAiProcessingAcknowledged] = useState(false);
  const mailboxState = requiresReconnect ? 'reconnect_required' : resolvedMailboxStatus.state;
  const mailboxBusy = ACTIVE_STATES.has(String(mailboxState || '').toLowerCase());
  const lastChecked = formatDateTime(resolvedMailboxStatus.last_checked_at || resolvedMailboxStatus.last_success_at);
  const mailboxUnavailable = resolvedMailboxStatus.available === false;

  return (
    <div className="settings-panel-stack">
      <section className="settings-panel settings-integration-panel">
        <div className="settings-panel-header">
          <div>
            <h3>Outlook</h3>
            <span className="subtext">Let Jane read your mailbox for search and summaries. CaseDB cannot send, delete, move, or modify email through this connection.</span>
          </div>
          <span className={`settings-integration-status ${connected && !requiresReconnect ? 'connected' : ''}`}>
            {loading ? 'Checking…' : connected && !requiresReconnect ? 'Connected' : requiresReconnect ? 'Reconnect needed' : 'Not connected'}
          </span>
        </div>

        <div className="settings-integration-body">
          <div className="settings-integration-icon"><Mail size={24} /></div>
          <div className="settings-integration-copy">
            {connected ? (
              <>
                <strong>{connection.display_name || connection.mailbox_email || 'Microsoft Outlook'}</strong>
                <span className="subtext">{connection.mailbox_email}</span>
                <span className="small-text">Connected {formatDateTime(connection.connected_at) || 'to this CaseDB account'}.</span>
              </>
            ) : (
              <>
                <strong>{requiresReconnect ? 'Reconnect Outlook' : 'Connect your Outlook account'}</strong>
                <span className="subtext">Jane will only receive the delegated read access you approved for your own mailbox.</span>
              </>
            )}
          </div>
        </div>

        <div className="settings-cookie-explanation">
          <ShieldCheck size={18} />
          <div>
            <strong>Read-only and account-specific</strong>
            <span className="subtext">Your Outlook authorization is stored separately from the existing CaseDB calendar and email-sending connection. Messages needed for a Jane answer or summary may be processed by one or more third-party AI providers configured for CaseDB.</span>
          </div>
        </div>

        {!connected && (
          <label className="settings-outlook-processing-consent">
            <input
              type="checkbox"
              checked={aiProcessingAcknowledged}
              onChange={(event) => setAiProcessingAcknowledged(event.target.checked)}
            />
            <span>I understand and approve that AI processing for the mailbox content Jane needs to answer my requests.</span>
          </label>
        )}

        <div className="settings-panel-actions settings-integration-actions">
          {confirmingDisconnect ? (
            <>
              <span className="subtext">Disconnecting pauses Outlook summary automations and removes Jane’s local mailbox access. Prior Outlook answers and summaries remain in their private Jane chats until you delete those chats. It does not change your organization’s Microsoft settings.</span>
              <button type="button" className="action tertiary" onClick={onCancelDisconnect} disabled={Boolean(working)}>Keep connected</button>
              <button type="button" className="action delete" onClick={onConfirmDisconnect} disabled={Boolean(working)}>
                {working === 'disconnect' ? <><Loader2 size={15} className="spinner" /> Disconnecting…</> : 'Disconnect'}
              </button>
            </>
          ) : connected ? (
            <button type="button" className="action tertiary outlook-connector" onClick={onRequestDisconnect} disabled={Boolean(working)}>Disconnect Outlook</button>
          ) : requiresReconnect ? (
            <>
              <button type="button" className="action tertiary outlook-connector" onClick={onRequestDisconnect} disabled={Boolean(working)}>Disconnect Outlook</button>
              <button type="button" className="action" onClick={() => onConnect?.({ acknowledgeAiProcessing: true })} disabled={loading || unavailable || Boolean(working) || !aiProcessingAcknowledged}>
                {working === 'connect' ? <><Loader2 size={15} className="spinner" /> Connecting…</> : 'Reconnect Outlook'}
              </button>
            </>
          ) : (
            <button type="button" className="action" onClick={() => onConnect?.({ acknowledgeAiProcessing: true })} disabled={loading || unavailable || Boolean(working) || !aiProcessingAcknowledged}>
              {working === 'connect' ? <><Loader2 size={15} className="spinner" /> Connecting…</> : 'Connect Outlook'}
            </button>
          )}
        </div>
        {unavailable && <span className="small-text settings-integration-error">The Outlook connector is not available in this environment yet.</span>}
      </section>

      <section className="settings-panel settings-outlook-feature-panel" aria-label="Outlook mailbox status">
        <div className="settings-panel-header">
          <div>
            <h3>Mailbox status</h3>
            <span className="subtext">Jane checks your Outlook mailbox live when you ask a mailbox question or request a summary.</span>
          </div>
          <span className={`settings-integration-status ${mailboxState === 'complete' ? 'connected' : ''}`}>
            {mailboxBusy && <Loader2 size={13} className="spinner" />}
            {mailboxStateCopy(mailboxState)}
          </span>
        </div>

        <div className="settings-outlook-status-grid">
          <div>
            <span className="small-text">Last checked</span>
            <strong>{lastChecked || 'Not yet'}</strong>
          </div>
        </div>

        {resolvedMailboxStatus.safe_error && (
          <span className="small-text settings-integration-error">{resolvedMailboxStatus.safe_error}</span>
        )}
        {mailboxUnavailable && connected && (
          <span className="small-text settings-integration-error">Mailbox checks are not available on this server yet.</span>
        )}

        <div className="settings-panel-actions settings-integration-actions">
          <span className="subtext">Check now confirms that Jane can reach your mailbox.</span>
          <button type="button" className="action tertiary" onClick={onCheckNow} disabled={controlsDisabled || mailboxBusy || mailboxUnavailable}>
            {working === 'check' || mailboxBusy
              ? <><Loader2 size={15} className="spinner" /> Checking…</>
              : <><RefreshCw size={15} /> Check now</>}
          </button>
        </div>
      </section>

    </div>
  );
};

export default OutlookIntegrationPanel;

